import { appManager } from '@inpart-io/devtools';
import { type IPartnerStatus, partnerServiceManager } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { PERSISTENCE_KEY } from '@/constants/persistence.constants';
import { getDateTime, isBefore, subSeconds } from '@/services/date-format';
import { SESSION_MODULE } from '@/store/session-module';
import { GET_TENANT_USER_CURRENT_KEY } from '@/store/session-module/getters';

import {
    STORE_ALLOW_NAVIGATION,
    STORE_CURRENT_BRIEF_ID,
    STORE_DIRECTORY_ASSET_EXPANSION,
    STORE_DIRECTORY_COMPANY_EXPANSION,
    STORE_EMAIL_PREVIEW_TEXT,
    STORE_MEETING_LIST_VIEW,
    STORE_PARTNER_STATUS,
    STORE_RECENT_AGREEMENT_BOARD_ID,
    STORE_RECENT_ALLIANCE_BOARD_ID,
    STORE_RECENT_COMPANY_BOARD_ID,
    STORE_RECENT_LEAD_BOARD_ID,
    STORE_RECENT_LISTS,
    STORE_RECENT_MEETING_BOARD_ID,
    STORE_RECENT_OBLIGATION_BOARD_ID,
    STORE_RECENT_VIEWED_BRIEF_ID,
    STORE_RECENT_VIEWED_CAMPAIGN_ID,
    STORE_RECENT_VIEWED_COMPANY_ID,
    STORE_RECENT_VIEWED_LEAD_ID,
    STORE_SETTINGS_EXPANSION
} from './mutations';
import { type IApplicationState, type IDirectoryAssetExpansion, type IDirectoryCompanyExpansion, type ISettingsExpansion } from './types';

export const ADD_RECENT_VIEWED_LEAD_ID = 'addRecentViewedLeadId';
export const ADD_RECENT_VIEWED_COMPANY_ID = 'addRecentViewedCompanyId';
export const ADD_RECENT_VIEWED_BRIEF_ID = 'addRecentViewedBriefId';
export const ADD_RECENT_VIEWED_CAMPAIGN_ID = 'addRecentViewedCampaignId';
export const LOAD_RECENT_LISTS = 'loadRecentListFromStore';
export const SET_MEETING_LIST_VIEW = 'setMeetingListView';
export const RETRIEVE_PARTNER_STATUS = 'retrievePartnerStatus';
export const LOAD_EMAIL_PREVIEW_PREFERENCE = 'loadEmailPreviewPreference';
export const SET_EMAIL_PREVIEW_TEXT = 'setEmailPreviewText';
export const SET_CURRENT_BRIEF_ID = 'setCurrentBriefIdState';
export const SET_RECENT_COMPANY_BOARD_ID = 'setRecentCompanyBoardIdState';
export const SET_RECENT_LEAD_BOARD_ID = 'setRecentBoardIdState';
export const SET_RECENT_MEETING_BOARD_ID = 'setRecentMeetingBoardIdState';
export const SET_RECENT_AGREEMENT_BOARD_ID = 'setRecentAgreementBoardIdState';
export const SET_RECENT_ALLIANCE_BOARD_ID = 'setRecentAllianceBoardIdState';
export const SET_RECENT_OBLIGATION_BOARD_ID = 'setRecentObligationBoardIdState';
export const SET_SETTINGS_EXPANSION = 'setSettingsExpansion';
export const SET_DIRECTORY_COMPANY_EXPANSION = 'setDirectoryCompanyExpansion';
export const SET_DIRECTORY_ASSET_EXPANSION = 'setDirectoryAssetExpansion';
export const ALLOW_NAVIGATION = 'allowNavigation';
export const PREVENT_NAVIGATION = 'preventNavigation';

let partnerStatusLastCheck = null as Date | null;
let partnerStatusPromise = null as Promise<IPartnerStatus> | null;
const lifeSpan = 30; // in seconds

export const actions: ActionTree<IApplicationState, unknown> = {
    async [RETRIEVE_PARTNER_STATUS](context): Promise<IPartnerStatus> {
        if (partnerStatusLastCheck !== undefined && isBefore(subSeconds(getDateTime(), lifeSpan), partnerStatusLastCheck)) {
            return Promise.resolve(partnerStatusPromise);
        }
        partnerStatusLastCheck = getDateTime();
        partnerStatusPromise = partnerServiceManager.getPartnerStatus().then((response) => {
            context.commit(STORE_PARTNER_STATUS, response.status);
            return response;
        });
        return partnerStatusPromise;
    },
    [LOAD_EMAIL_PREVIEW_PREFERENCE](context) {
        const userKey: string = context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`];

        void appManager.persistenceManager.getItem<boolean>(`${PERSISTENCE_KEY.EMAIL_PREVIEW_TEXT}.${userKey}`).then((emailPreviewText = false) => {
            context.commit(STORE_EMAIL_PREVIEW_TEXT, { userKey, emailPreviewText });
        });
    },
    [SET_EMAIL_PREVIEW_TEXT](context, { emailPreviewText = false }: { emailPreviewText: boolean }) {
        const userKey: string = context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`];

        context.commit(STORE_EMAIL_PREVIEW_TEXT, { userKey, emailPreviewText });
    },
    [ALLOW_NAVIGATION](context, { key }: { key: string }) {
        context.commit(STORE_ALLOW_NAVIGATION, { key, add: false });
    },
    async [LOAD_RECENT_LISTS](context) {
        const userKey: string = context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`];

        const meetingsView = await appManager.persistenceManager.getItem<string>(`${PERSISTENCE_KEY.MEETING_LIST_VIEW}.${userKey}`);
        if (meetingsView) {
            context.commit(STORE_MEETING_LIST_VIEW, { view: meetingsView, userKey });
        }

        const recentViewedLeadsIds = await appManager.persistenceManager.getItem<number[]>(`${PERSISTENCE_KEY.RECENT_VIEWED_LEADS_IDS}.${userKey}`);
        const recentViewedCompaniesIds = await appManager.persistenceManager.getItem<number[]>(`${PERSISTENCE_KEY.RECENT_VIEWED_COMPANIES_IDS}.${userKey}`);
        const recentViewedBriefsIds = await appManager.persistenceManager.getItem<number[]>(`${PERSISTENCE_KEY.RECENT_VIEWED_BRIEFS_IDS}.${userKey}`);
        const recentViewedCampaignsIds = await appManager.persistenceManager.getItem<number[]>(`${PERSISTENCE_KEY.RECENT_VIEWED_CAMPAIGNS_IDS}.${userKey}`);
        context.commit(STORE_RECENT_LISTS, { userKey, recentViewedLeadsIds, recentViewedCompaniesIds, recentViewedBriefsIds, recentViewedCampaignsIds });

        const settingsExpansion = await appManager.persistenceManager.getItem<ISettingsExpansion>(`${PERSISTENCE_KEY.SETTINGS_EXPANSION}.${userKey}`);
        if (settingsExpansion) {
            context.commit(STORE_SETTINGS_EXPANSION, { expansion: settingsExpansion, userKey });
        }

        const directoryAssetExpansion = await appManager.persistenceManager.getItem<IDirectoryAssetExpansion>(
            `${PERSISTENCE_KEY.DIRECTORY_ASSET_EXPANSION}.${userKey}`
        );
        if (directoryAssetExpansion) {
            context.commit(STORE_DIRECTORY_ASSET_EXPANSION, { expansion: directoryAssetExpansion, userKey });
        }
        const directoryCompanyExpansion = await appManager.persistenceManager.getItem<IDirectoryCompanyExpansion>(
            `${PERSISTENCE_KEY.DIRECTORY_COMPANY_EXPANSION}.${userKey}`
        );
        if (directoryCompanyExpansion) {
            context.commit(STORE_DIRECTORY_COMPANY_EXPANSION, { expansion: directoryCompanyExpansion, userKey });
        }

        const briefId = await appManager.persistenceManager.getItem<number>(PERSISTENCE_KEY.CURRENT_BRIEF_ID);
        if (briefId) {
            context.commit(STORE_CURRENT_BRIEF_ID, briefId);
        }

        const boardId = await appManager.persistenceManager.getItem<number>(`${PERSISTENCE_KEY.RECENT_LEAD_BOARD_ID}.${userKey}`);
        if (boardId) {
            context.commit(STORE_RECENT_LEAD_BOARD_ID, { boardId, userKey });
        }

        const meetingBoardId = await appManager.persistenceManager.getItem<number>(`${PERSISTENCE_KEY.RECENT_MEETING_BOARD_ID}.${userKey}`);
        if (meetingBoardId) {
            context.commit(STORE_RECENT_MEETING_BOARD_ID, { boardId: meetingBoardId, userKey });
        }

        const companyBoardId = await appManager.persistenceManager.getItem<number>(`${PERSISTENCE_KEY.RECENT_COMPANY_BOARD_ID}.${userKey}`);
        if (companyBoardId) {
            context.commit(STORE_RECENT_COMPANY_BOARD_ID, { boardId: companyBoardId, userKey });
        }

        const agreementBoardId = await appManager.persistenceManager.getItem<number>(`${PERSISTENCE_KEY.RECENT_AGREEMENT_BOARD_ID}.${userKey}`);
        if (agreementBoardId) {
            context.commit(STORE_RECENT_AGREEMENT_BOARD_ID, { boardId: agreementBoardId, userKey });
        }

        const allianceBoardId = await appManager.persistenceManager.getItem<number>(`${PERSISTENCE_KEY.RECENT_ALLIANCE_BOARD_ID}.${userKey}`);
        if (allianceBoardId) {
            context.commit(STORE_RECENT_ALLIANCE_BOARD_ID, { boardId: allianceBoardId, userKey });
        }
    },
    [PREVENT_NAVIGATION](context, { key }: { key: string }) {
        context.commit(STORE_ALLOW_NAVIGATION, { key, add: true });
    },
    [SET_CURRENT_BRIEF_ID](context, { briefId }: { briefId: number }) {
        context.commit(STORE_CURRENT_BRIEF_ID, briefId);
    },
    [SET_RECENT_COMPANY_BOARD_ID](context, { boardId }: { boardId: number }) {
        context.commit(STORE_RECENT_COMPANY_BOARD_ID, { boardId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_RECENT_LEAD_BOARD_ID](context, { boardId }: { boardId: number }) {
        context.commit(STORE_RECENT_LEAD_BOARD_ID, { boardId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_RECENT_MEETING_BOARD_ID](context, { boardId }: { boardId: number }) {
        context.commit(STORE_RECENT_MEETING_BOARD_ID, { boardId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_RECENT_AGREEMENT_BOARD_ID](context, { boardId }: { boardId: number }) {
        context.commit(STORE_RECENT_AGREEMENT_BOARD_ID, { boardId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_RECENT_ALLIANCE_BOARD_ID](context, { boardId }: { boardId: number }) {
        context.commit(STORE_RECENT_ALLIANCE_BOARD_ID, { boardId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_RECENT_OBLIGATION_BOARD_ID](context, { boardId }: { boardId: number }) {
        context.commit(STORE_RECENT_OBLIGATION_BOARD_ID, { boardId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_MEETING_LIST_VIEW](context, { view }: { view: string }) {
        context.commit(STORE_MEETING_LIST_VIEW, { view, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [ADD_RECENT_VIEWED_LEAD_ID](context, { leadId }: { leadId: number }) {
        context.commit(STORE_RECENT_VIEWED_LEAD_ID, { leadId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [ADD_RECENT_VIEWED_COMPANY_ID](context, { companyId }: { companyId: number }) {
        context.commit(STORE_RECENT_VIEWED_COMPANY_ID, { companyId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [ADD_RECENT_VIEWED_BRIEF_ID](context, { briefId }: { briefId: number }) {
        context.commit(STORE_RECENT_VIEWED_BRIEF_ID, { briefId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [ADD_RECENT_VIEWED_CAMPAIGN_ID](context, { campaignId }: { campaignId: number }) {
        context.commit(STORE_RECENT_VIEWED_CAMPAIGN_ID, { campaignId, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_SETTINGS_EXPANSION](context, { expansion }: { expansion: ISettingsExpansion }) {
        context.commit(STORE_SETTINGS_EXPANSION, { expansion, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_DIRECTORY_COMPANY_EXPANSION](context, { expansion }: { expansion: IDirectoryCompanyExpansion }) {
        context.commit(STORE_DIRECTORY_COMPANY_EXPANSION, { expansion, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    },
    [SET_DIRECTORY_ASSET_EXPANSION](context, { expansion }: { expansion: IDirectoryAssetExpansion }) {
        context.commit(STORE_DIRECTORY_ASSET_EXPANSION, { expansion, userKey: context.rootGetters[`${SESSION_MODULE}/${GET_TENANT_USER_CURRENT_KEY}`] });
    }
};
